import { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { useAuthService } from "../../Context/AuthContext";
import { PLATFORM_URL, TOOL_HINT_LIMIT, TOOL_HINT_PROBABILITY } from "../../Constants/constantValues";
import LocalStorage from "../../Helpers/LocalStorage";
import InfoModal from "./InfoModal";

const MeetingsAccessGate = ({ title, icon, hintKey, children }) => {
  const { t } = useTranslate();
  const { authHasCompleted, platformMeetings } = useAuthService();
  const [showHintModal, setShowHintModal] = useState(false);

  useEffect(() => {
    if (!platformMeetings?.enabled || !hintKey) return;

    const current = parseInt(LocalStorage.getToolHintCounter(hintKey) || "0", 10);
    const next = current + 1;

    if (next >= TOOL_HINT_LIMIT) {
      LocalStorage.setToolHintCounter(hintKey, "0");
    } else {
      LocalStorage.setToolHintCounter(hintKey, String(next));
    }

    if (next === 1 || Math.random() < TOOL_HINT_PROBABILITY) {
      setShowHintModal(true);
    }
  }, [platformMeetings?.enabled, hintKey]);

  if (authHasCompleted === null || platformMeetings === null) {
    return <Wrapper />;
  }

  if (!platformMeetings?.enabled) {
    return (
      <Wrapper>
        <Content>
          <NoAccessCard>
            <NoAccessIcon>{icon}</NoAccessIcon>
            <NoAccessTitle>{title}</NoAccessTitle>
            <NoAccessMessage>{t("meetings_gate.no_access_message", { tool_name: title })}</NoAccessMessage>
            <NoAccessLink
              onClick={async () => {
                const currentToken = LocalStorage.getAuthenticationToken();
                if (!currentToken) { window.open(PLATFORM_URL); return; }
                const { getAuthBridgeExchangeToken } = await import("../../API/authBridge");
                const exchangeToken = await getAuthBridgeExchangeToken(currentToken);
                if (exchangeToken) {
                  if (window.electron?.openExternalUrl) {
                    window.electron.openExternalUrl(`${PLATFORM_URL}/login?bridge=${exchangeToken}`);
                  } else {
                    window.open(`${PLATFORM_URL}/login?bridge=${exchangeToken}`);
                  }
                } else {
                  window.open(PLATFORM_URL);
                }
              }}
            >
              {t("meetings_gate.go_to_platform")}
            </NoAccessLink>
          </NoAccessCard>
        </Content>
      </Wrapper>
    );
  }

  return (
    <>
      {children}
      {showHintModal && (
        <InfoModal
          icon={icon}
          title={title}
          message={t("meetings_gate.usage_hint", { tool_name: title })}
          confirmText={t("general.got_it")}
          onClose={() => setShowHintModal(false)}
        />
      )}
    </>
  );
};

export default MeetingsAccessGate;

const Wrapper = styled.div`
  min-height: 100%;
  width: 100%;
  padding: 48px 24px 64px;
  background: ${({ theme }) => theme.menu.background};
  box-sizing: border-box;
  overflow-y: auto;
`;

const Content = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const NoAccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
`;

const NoAccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(112, 105, 253, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  color: rgba(112, 105, 253, 0.5);
`;

const NoAccessTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
`;

const NoAccessMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
  max-width: 360px;
  line-height: 1.6;
`;

const NoAccessLink = styled.a`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(112, 105, 253, 1);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

